import { useQuery, useInfiniteQuery } from "@tanstack/react-query";
import { getPublicServerEndpoint } from "../services/helper";

const fetchCollections = async (storeId, source = "featured") => {
  const response = await fetch(
    `${getPublicServerEndpoint()}/v3/collection/store/${storeId}${source ? `?source=${source}` : ""}`,
  );
  if (!response.ok) {
    throw new Error("Failed to fetch collections");
  }
  return response.json();
};

const fetchCollectionsPage = async ({ storeId, pageParam = 1, limit = 5 }) => {
  try {
    const response = await fetch(
      `${getPublicServerEndpoint()}/v3/collection/store/${storeId}?limit=${limit}&page=${pageParam}`,
    );
    if (!response.ok) {
      throw new Error(`Failed to fetch collections: ${response.status} ${response.statusText}`);
    }
    return response.json();
  } catch (error) {
    console.error(`Error fetching collections page ${pageParam}:`, error);
    throw error;
  }
};

const useCollection = (storeId, source = "featured") => {
  return useQuery(["collections", storeId, source], () => fetchCollections(storeId, source), {
    enabled: !!storeId,
  });
};

/**
 * Hook for fetching collections with pagination
 * @param {string} storeId - Store ID to fetch collections for
 * @param {number} limit - Number of collections per page
 * @returns {Object} - React Query infinite query result
 */
const usePaginatedCollections = (storeId, limit = 5) => {
  return useInfiniteQuery(
    ["paginatedCollections", storeId, limit],
    ({ pageParam = 1 }) => fetchCollectionsPage({ storeId, pageParam, limit }),
    {
      enabled: !!storeId,
      getNextPageParam: (lastPage, allPages) => {
        // Use the pagination.pages from the response to determine if there are more pages
        if (!lastPage.pagination || !lastPage.pagination.pages) {
          return undefined; // No pagination info available
        }

        const currentPage = allPages.length;
        const totalPages = lastPage.pagination.pages;

        // Return undefined if we're at the last page to prevent extra API calls
        return currentPage < totalPages ? currentPage + 1 : undefined;
      },
    },
  );
};

const fetchCollectionsWithProducts = async ({
  storeId,
  pageParam = 1,
  limit = 5,
  productsPerCollection = 10,
}) => {
  try {
    const response = await fetch(
      `${getPublicServerEndpoint()}/v4/collection/store/${storeId}/with-products?page=${pageParam}&limit=${limit}&productsPerCollection=${productsPerCollection}`,
    );

    if (!response.ok) {
      throw new Error(
        `Failed to fetch collections with products: ${response.status} ${response.statusText}`,
      );
    }

    return response.json();
  } catch (error) {
    console.error(`Error fetching collections with products page ${pageParam}:`, error);
    throw error;
  }
};

/**
 * Hook for fetching collections with products in a single API call
 * @param {string} storeId - Store ID to fetch collections for
 * @param {number} limit - Number of collections per page
 * @param {number} productsPerCollection - Number of products per collection
 * @returns {Object} - React Query infinite query result
 */
const useCollectionsWithProducts = (storeId, limit = 5, productsPerCollection = 10) => {
  return useInfiniteQuery(
    ["collectionsWithProducts", storeId, limit, productsPerCollection],
    ({ pageParam = 1 }) =>
      fetchCollectionsWithProducts({ storeId, pageParam, limit, productsPerCollection }),
    {
      enabled: !!storeId,
      getNextPageParam: (lastPage, allPages) => {
        if (!lastPage.pagination || !lastPage.pagination.pages) {
          return undefined;
        }

        const currentPage = allPages.length;
        const totalPages = lastPage.pagination.pages;

        return currentPage < totalPages ? currentPage + 1 : undefined;
      },
    },
  );
};

export { usePaginatedCollections, useCollectionsWithProducts };
export default useCollection;
